import React, { Fragment, SyntheticEvent, useContext, useEffect, useState } from 'react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import profilePlaceholder from '../../assets/images/profile/profile-placeholder.png';
import backButtonImage from '../../assets/svg/back.svg';
import editProjectImage from '../../assets/svg/edit.svg';
import mailIcon from '../../assets/svg/mail.svg';
import plusCircleSolid from '../../assets/svg/plus-circle-solid.svg';
import commentIcon from '../../assets/svg/comment.svg';
import { MessageContext, MessageContextProvider } from '../../contexts/messageContext';
import {
  ProjectContext,
  ProjectContextProvider
} from '../../contexts/projectContext';
import { UserContext, UserContextProvider } from '../../contexts/userContext';
import RoomListsContextProvider from '../../contexts/roomListsContext';
import { GET_CURRENT_USER_me } from '../../graphql/types/GET_CURRENT_USER';
import { GET_PROJECT_getProject } from '../../graphql/types/GET_PROJECT';
import { ProjectFollower } from '../../graphql/types/shared';
import { useRedirect } from '../../hooks/useRedirect';
import { IProjectShowPageProps } from '../../pages/project/show';
import { getCompressedImagePath } from '../../utils/getCompressedImagePath';
import { PrettyContent } from '../Content';
import ImageSlider from '../ImageSlider';
import Tab from '../Tab';
import InterestedProject from './Action/Interested';
import LikeProject from './Action/LikeProject';
import ShareLink from './Action/ShareLink';
import FollowProject from './Action/FollowProject';
import Collaborators from './ProjectCollaborator';
import Comment from './ProjectComment';
import Story from './ProjectStory';
import ProjectTags from './ProjectTags';
import Update from './ProjectUpdate';
import { accessTokenStorage } from '../../services/auth';
import MatchCard from '../Match/MatchCard';
import { useQuery } from '@apollo/client';
import { GET_RECOMMENDED_USERS_FOR_PROJECT } from '../../graphql/queries/recommendedUsers';
import { GET_USERS_BY_IDS } from '../../graphql/GET_USERS_BY_IDS';
import { CurrentUserContextProvider } from '../../contexts/currentUserContext';

interface IProjectContentProps {
  user: GET_CURRENT_USER_me;
}

interface ISidebarContentProps {
  project: GET_PROJECT_getProject;
  compressedImage: string;
}

interface IUser {
  id?: number;
  name?: string;
  avatar?: string;
  location?: string;
  bio?: string;
  professions?: string[];
  collaborationRoles?: string[];
  status?: string;
  similarityScore?: number;
}

const ProjectContent = ({ user }: IProjectContentProps) => {
  const project = useContext(ProjectContext);
  const isProjectOwner =
    project && project.author && user && project.author.id === user.id;
  const [activeTab, setActiveTab] = useState('Story');

  return (
    <Fragment>
      <div className="content page-project-content is-bordered-bottom p-b-0 m-b-0">
        <header className="project-header">
          <div
            className={`level project-header-level ${
              !project.genres.length && !isProjectOwner && 'm-b-0'
            }`}
          >
            <div className="level-left" style={{ flexWrap: 'wrap', gap: '8px' }}>
              <ProjectTags genres={project.genres} />
            </div>
            {isProjectOwner && (
              <div className="level-right">
                <Link to={`/project/${project.id}/update`} className="is-flex">
                  <img
                    src={editProjectImage}
                    alt="Edit project"
                    className="image is-inline"
                  />
                  <span className="is-small">Edit</span>
                </Link>
              </div>
            )}
          </div>
          <div className="project-header-top is-bordered-bottom is-block">
            <h1 className="is-size-4 is-relative p-r-3">{project.title}</h1>
          </div>
          <div className="project-header-bottom">
            <div className="level is-small">
              <div className="level-left">
                {project.type && (
                  <p className="level-item is-capitalized">
                    {project.type.toLocaleLowerCase()}
                  </p>
                )}
                {project.locations && project.locations[0] && (
                  <p className="level-item is-capitalized">
                    {project.locations[0].name}
                  </p>
                )}
                <p className="level-item is-capitalized">
                  Funding:{' '}
                  {project.fundingStatus &&
                    project.fundingStatus.toLocaleLowerCase()}
                </p>
              </div>
              <div className="level-right">
                <p className="level-item">
                  Posted: &nbsp;
                  <Moment format="YYYY/MM/DD">{project.createdAt}</Moment>
                </p>
              </div>
            </div>
          </div>
        </header>
        <ImageSlider medias={project.medias} />
      </div>

      {/* ── Horizontal actions bar ── */}
      <div className="level is-bordered-bottom p-t-1 p-b-1 p-r-1 p-l-1 m-b-0">
        <div className="level-left">
          <div className="level-item">
            <LikeProject
              user={user}
              pageType={'project'}
              postId={project.id}
              likes={project.likes ? project.likes : []}
              wasLikedByUser={
                project.likes &&
                Boolean(
                  project.likes.find((like) => like.author.id === user.id)
                )
                  ? true
                  : false
              }
            />
          </div>
          <div className="level-item">
            <a
              className="post-card-stats-item is-inline-flex has-text-grey-dark has-text-weight-semibold is-small has-cursor-pointer m-r-0"
              onClick={() => setActiveTab('Comment')}
            >
              <span className="post-card-comment-icon-wrapper">
                <img src={commentIcon} alt="comment icon" style={{ width: '24px', height: '24px' }} />
                {project.comments.length > 0 && (
                  <span className="post-card-comment-badge is-active" />
                )}
              </span>
              <span>{project.comments.length}</span>
            </a>
          </div>
          <div className="level-item">
            <FollowProject
              user={user}
              projectId={project.id}
              followers={project.followers?.filter(f => f.followBy !== null) as ProjectFollower[] ?? []}
              wasFollowedByUser={
                project.followers && user
                  ? Boolean(
                      project.followers.find(
                        (follow) => follow?.followBy?.id === user?.id
                      )
                    )
                  : false
              }
              projectAuthorId={project.author?.id ?? ''}
              currentUserId={user?.id ?? ''}
            />
          </div>
          <div className="level-item">
            <ShareLink project={project} />
          </div>
        </div>
        <div className="level-right">
          <div className="level-item">
            <InterestedProject
              user={user}
              pageType={'project'}
              projectId={project.id}
              interested={project.interested}
              wasInterestedByUser={
                project.interested &&
                Boolean(
                  project.interested.find(
                    (interested) => interested.author.id === user.id
                  )
                )
              }
            />
          </div>
        </div>
      </div>
      <Tab
        contents={[
          {
            title: 'Story',
            contentCount: 0,
            body: <CurrentUserContextProvider><Story /></CurrentUserContextProvider>
          },
          {
            title: 'Update',
            contentCount: project.updates.length,
            body: <Update user={user} />
          },
          {
            title: 'Collaborators',
            contentCount: project.collaborations
              ? project.collaborations.length
              : 0,
            body: <Collaborators user={user} />
          },
          {
            title: 'Comment',
            contentCount: project.comments.length,
            body: <Comment user={user} />
          }
        ]}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />
    </Fragment>
  );
};

// Content for ProjectSidebar
const SidebarContent = ({ project, compressedImage }: ISidebarContentProps) => {
  const user = useContext(UserContext);
  const { actions } = useContext(MessageContext);
  const [isSelecting, setIsSelecting] = useState(false);

  const handleSendMessageOnClick = async (e: SyntheticEvent) => {
    e.preventDefault();
    setIsSelecting(true);
    try {
      await actions.handleSelectOtherUser(user);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSelecting(false);
    }
  };

  return (
    <div className="card is-dropshadowed">
      <header className="card-header is-bordered-bottom m-b-0">
        <p className="card-header-title has-text-weight-normal">
          <span className="is-small is-light">Project by</span>
        </p>
      </header>
      <div className="card-content">
        <div className="media">
          <div className="media-left">
            <figure
              className="image avatar is-48x48"
              style={{ backgroundImage: `url(${compressedImage})` }}
            />
          </div>
          <div className="media-content">
            <p className="is-size-5 m-b-0">
              <Link
                to={`/user/${project.author && project.author.id}`}
                className="has-text-black"
              >
                {project.author &&
                  project.author.profile &&
                  project.author.profile.name}
              </Link>
            </p>
            <p>
              {project.author &&
                project.author.professions &&
                project.author.professions.map((profession, idx) => (
                  <span key={idx} className="is-small is-light">
                    {(idx ? ', ' : '') + profession.name}
                  </span>
                ))}
            </p>
          </div>
        </div>

        <div className="content">
          <p className="is-small">
            {project.author &&
              project.author.profile &&
              project.author.profile.bio && (
                <PrettyContent
                  content={project.author.profile.bio}
                  isLineBreakEnabled={true}
                />
              )}
          </p>
        </div>
      </div>
      <footer className="card-footer">
        <Link
          to={`/user/${
            project.author && project.author.id && project.author.id
          }`}
          className="card-footer-item is-small has-text-grey-dark"
        >
          Visit Profile
        </Link>

        <div
          className="card-footer-item is-small has-text-grey-dark has-cursor-pointer"
          onClick={handleSendMessageOnClick}
        >
          <img
            src={mailIcon}
            alt="mail icon"
            className="image is-inline"
            width="18px"
            title="Send a message"
          />
          <span>{isSelecting ? 'Loading' : 'Message'}</span>
        </div>
      </footer>
    </div>
  );
};
const ProjectSideBar = ({ user }: IProjectContentProps) => {
  const project = useContext(ProjectContext);
  const isProjectOwner =
    project && project.author && user && project.author.id === user.id;
  const [, setMatchedUser] = useState<IUser>();
  const [showMatchCard, setShowMatchCard] = useState(true);

  const [hiddenMatchUsers, setHiddenMatchUsers] = useState<string[]>(() => {
    const savedHiddenUsers = localStorage.getItem(`hiddenMatchUsers_${project.id}`);
    return savedHiddenUsers ? JSON.parse(savedHiddenUsers) : [];
  });

  // Fetch recommended user IDs
  const { data: recommendationData } = useQuery(GET_RECOMMENDED_USERS_FOR_PROJECT, {
    variables: {
      projectId: Number(project.id),
      topN: 5
    },
    skip: !project.id
  });

  // Fetch user details for the recommended users
  const { data: userData } = useQuery(GET_USERS_BY_IDS, {
    variables: {
      ids: [...(recommendationData?.getRecommendedUsersForProject?.userIds || [])]
    },
    skip: !recommendationData?.getRecommendedUsersForProject?.userIds?.length
  });

  const fourthUser = userData?.getUsersByIds?.[3] || null;
  useEffect(() => {
    if (fourthUser) {
      setMatchedUser(fourthUser);
    }
  }, [fourthUser])

  const handleStartChat = () => {
    setMatchedUser({ name: '' })
  };

  const handleMaybeLater = () => {
    if (fourthUser && fourthUser.id) {
      const updatedHiddenUsers = [...hiddenMatchUsers, fourthUser.id];
      setHiddenMatchUsers(updatedHiddenUsers);
      localStorage.setItem(
        `hiddenMatchUsers_${project.id}`,
        JSON.stringify(updatedHiddenUsers)
      );
    }
    setMatchedUser({ name: '' });
    setShowMatchCard(false);
  };

  const renderProjectAuthor = (project: GET_PROJECT_getProject) => {
    const authorAvatarImgUrl =
      (project.author &&
        project.author.avatar &&
        project.author.avatar.fileUrl &&
        project.author.avatar.fileUrl) ??
      profilePlaceholder;

    const compressedImage = getCompressedImagePath(
      authorAvatarImgUrl,
      'thumbnail'
    );

    return (
      <UserContextProvider userId={project.author!.id}>
        <RoomListsContextProvider>
          <SidebarContent project={project} compressedImage={compressedImage} />
        </RoomListsContextProvider>
      </UserContextProvider>
    );
  };

  return (
    <Fragment>
      {/* Sidebar - Project Author */}
      {renderProjectAuthor(project)}

      {/* Sidebar - Fans */}
      <div className="card page-project-sidebar-fans is-dropshadowed">
        <header className="card-header is-block is-shadowless">
          <h3 className="card-header-title has-text-weight-normal is-small">
            Promote this project
          </h3>
          <form action="#">
            <div className="field">
              <div className="control has-icons-right p-r-1 p-l-1">
                <input
                  className="input is-small"
                  type="text"
                  placeholder="Recommend a friend"
                />
                <span className="icon is-small is-right">
                  <img
                    width="14px"
                    className="m-r-1"
                    src={plusCircleSolid}
                    alt="plus circle"
                  />
                </span>
              </div>
            </div>
          </form>
        </header>
        <div className="card-content">
          <h3 className="has-text-weight-normal is-small m-b-1">
            Fans{' '}
            <Link to="#" className="is-light is-pulled-right">
              {project.likes
                ? project.likes.length > 1
                  ? `${project.likes.length} fans`
                  : `${project.likes.length} fan`
                : 0}
            </Link>
          </h3>
          <div className="columns page-project-sidebar-fans-columns is-multiline is-mobile is-table">
            {project.likes &&
              project.likes.map((like) => {
                const likeBgImage =
                  like && like.author.avatar && like.author.avatar.fileUrl;
                const compressedLikeBgImage = getCompressedImagePath(
                  likeBgImage,
                  'thumbnail'
                );
                return (
                  <div className="column is-pulled-left" key={like.id}>
                    <Link
                      to={`/user/${like.author && like.author.id}`}
                      className="tooltip"
                    >
                      <figure
                        className="image is-42x42 is-rounded page-project-list-sidebar-fans"
                        style={{
                          backgroundImage: `url(${
                            compressedLikeBgImage || profilePlaceholder
                          })`
                        }}
                      />
                      <span className="tooltiptext has-text-centered is-size-8">
                        {like.author.profile.name}
                      </span>
                    </Link>
                  </div>
                );
              })}
          </div>

          {!isProjectOwner &&
            project?.author?.stripeAccountStatus === 'complete' && (
              <div>
                <h3 style={{ marginBottom: '0.3rem' }}>Fund Project</h3>
                <form
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    paddingBottom: '1rem'
                  }}
                  onSubmit={async (e: any) => {
                    e.preventDefault();
                    const formData = new FormData(e.target);
                    const amount = formData.get('amount');

                    const res = await fetch(
                      `${process.env.REACT_APP_API_BASE_URL}/api/v1/stripe/create-checkout-session`,
                      {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          Authorization: `Bearer ${accessTokenStorage.get()}`
                        },
                        body: JSON.stringify({
                          amount,
                          connectedAccountId: project?.author?.stripeId,
                          projectId: project?.id,
                          receiverId: project?.author?.id,
                          senderId: user?.id
                        })
                      }
                    );

                    const data = await res.json();

                    if (data.error) {
                      window.alert(data.message);
                    }

                    window.location.href = data.sessionUrl;
                  }}
                >
                  <input
                    required
                    type="number"
                    name="amount"
                    placeholder="Amount"
                    min={5}
                    // onInput={(e: any) => {
                    //   if (e.target.value !== '' && e.target.value < 1) {
                    //     e.target.value = '';
                    //   }
                    // }}
                  />
                  <button type="submit" style={{ padding: '0.3rem 1.5rem' }}>
                    Pay
                  </button>
                </form>
              </div>
            )}
        </div>
      </div>
      <div className="m-t-1">
        {fourthUser && showMatchCard && (
          <RoomListsContextProvider>
            <MessageContextProvider currentUser={user}>
              <MatchCard
                userName={fourthUser?.profile?.name}
                userLocation={fourthUser?.profile?.location}
                userBio={fourthUser?.profile?.bio}
                userAvatar={fourthUser?.avatar?.fileUrl}
                onStartChat={handleStartChat}
                onMaybeLater={handleMaybeLater}
                user={fourthUser}
              />
            </MessageContextProvider>
          </RoomListsContextProvider>
        )}
      </div>
    </Fragment>
  );
};

const PrivateProject: React.FunctionComponent<IProjectShowPageProps> = (
  props: IProjectShowPageProps
) => {
  const { redirectToHomePage } = useRedirect();

  return (
    <Fragment>
      <div className="container">
        <div className="section p-b-0 project-control w-pc-25">
          {props.user && (
            <a
              href="#"
              className="has-text-weight-medium is-flex is-light is-small"
              onClick={() => redirectToHomePage()}
            >
              <span className="m-r-1">
                <img src={backButtonImage} alt="Back button" />
              </span>
              <strong>Back to projects</strong>
            </a>
          )}
        </div>
        <div className="columns section">
          <ProjectContextProvider projectId={props.match.params.projectId}>
            <div className="column is-three-quarters has-background-white is-paddingless is-rounded is-dropshadowed">
              <ProjectContent user={props.user!} />
            </div>
            <div className="column page-project-sidebar p-t-0">
              <ProjectSideBar user={props.user!} />
            </div>
          </ProjectContextProvider>
        </div>
      </div>
    </Fragment>
  );
};

export default PrivateProject;
